import Image from 'next/image';
import { useMemo } from 'react';
import { NETWORKS } from 'src/common/types/network';

import { KeyValueRow, KeyValueTable } from '@/design-system/components/ui';
import useGetChainInfo from '@/hooks/useGetChainInfo';

import { Trigger } from '../../domain';
import { AddressChip } from './components/AddressChip';

interface Props {
  trigger: Trigger;
}

export function EulerV2LoopingTriggerFullDetails({ trigger }: Props) {
  const { getChainIconUrl } = useGetChainInfo();

  const depositVaultAddress = trigger.context?.depositVault ?? '';
  const borrowVaultAddress = trigger.context?.borrowVault ?? '';
  const chainId = trigger.context?.chain_id ?? 1;
  const maxLeverage = trigger.context?.max_leverage;
  const maxLeverageOperator = trigger.context?.max_leverage_operator;

  const networkInfo = useMemo(() => {
    return NETWORKS.find((network) => network.chainId === chainId) ?? null;
  }, [chainId]);

  const networkDisplay = useMemo(() => {
    if (!networkInfo) return '-';
    const chainIconUrl = getChainIconUrl(networkInfo.chainId);

    return (
      <div className="tw:flex tw:flex-row tw:items-center tw:gap-2">
        {chainIconUrl && <Image src={chainIconUrl} alt={networkInfo.name} width={20} height={20} />}
        <span style={{ textTransform: 'capitalize' }}>{networkInfo.name.replace(/-/g, ' ')}</span>
      </div>
    );
  }, [networkInfo, getChainIconUrl]);

  const depositVaultDisplay = useMemo(() => {
    if (!depositVaultAddress) return '-';
    return <AddressChip address={depositVaultAddress} chainId={chainId} />;
  }, [depositVaultAddress, chainId]);

  const borrowVaultDisplay = useMemo(() => {
    if (!borrowVaultAddress) return '-';
    return <AddressChip address={borrowVaultAddress} chainId={chainId} />;
  }, [borrowVaultAddress, chainId]);

  const maxLeverageDisplay = useMemo(() => {
    if (!maxLeverage) return '-';
    const operatorSymbol = maxLeverageOperator === 'less_than_or_equal' ? '<=' : '<';
    return `${operatorSymbol} ${maxLeverage}`;
  }, [maxLeverage, maxLeverageOperator]);

  return (
    <KeyValueTable>
      <KeyValueRow label="Name" value={trigger.name} />
      <KeyValueRow label="Description" value={trigger.description} />
      <KeyValueRow label="Deposit Vault" value={depositVaultDisplay} />
      <KeyValueRow label="Borrow Vault" value={borrowVaultDisplay} />
      <KeyValueRow label="Network" value={networkDisplay} />
      <KeyValueRow label="Max Leverage" value={maxLeverageDisplay} />
      <KeyValueRow label="Argument conditions" value={trigger.conditionExpression ?? '-'} />
    </KeyValueTable>
  );
}
